import { Form, Formik, FormikHelpers, FormikProps, FormikValues } from "formik";
import {
  TypedFormikProps,
  useTypedFormikContext,
} from "hooks/useTypedFormikContext";
import { ComponentProps, forwardRef, Ref } from "react";

type FormikComponentProps = ComponentProps<typeof Formik>;

type FormWrapperProps<Values extends FormikValues = FormikValues> = {
  children:
    | ((props: TypedFormikProps<Values>) => React.ReactNode)
    | React.ReactNode;
  formikProps: FormikProps<Values>;
};

type FormikFormInnerProps<Values extends FormikValues> = {
  children:
    | ((props: TypedFormikProps<Values>) => React.ReactNode)
    | React.ReactNode;
  onSubmit: (
    values: Values,
    formikHelpers?: FormikHelpers<Values>
  ) => void | Promise<any>;
  initialValues: Values;
  validationSchema?: FormikComponentProps["validationSchema"];
  enableReinitialize?: FormikComponentProps["enableReinitialize"];
  validateOnMount?: FormikComponentProps["validateOnMount"];
  validateOnChange?: FormikComponentProps["validateOnChange"];
  validateOnBlur?: FormikComponentProps["validateOnBlur"];
  validationContext?: any;
  noValidate?: boolean;
  className?: string;
} & { ref?: Ref<HTMLFormElement> };

function FormWrapper<Values extends FormikValues>({
  children,
  formikProps,
}: FormWrapperProps<Values>) {
  const typedContext = useTypedFormikContext<Values>();

  return (
    <>
      {typeof children === "function"
        ? children({
            ...formikProps,
            ...typedContext,
          })
        : children}
    </>
  );
}

function FormikFormInner<Values extends FormikValues>(
  {
    children,
    onSubmit,
    initialValues,
    validationSchema,
    enableReinitialize = false,
    validateOnMount = false,
    validationContext,
    ...props
  }: FormikFormInnerProps<Values>,
  ref: Ref<HTMLFormElement>
) {
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={enableReinitialize}
      validateOnMount={validateOnMount}
      validateOnChange={props.validateOnChange}
      validateOnBlur={props.validateOnBlur}
      validationContext={validationContext}
      onSubmit={onSubmit}
    >
      {(formikProps) => (
        <Form ref={ref} {...props}>
          <FormWrapper formikProps={formikProps}>{children}</FormWrapper>
        </Form>
      )}
    </Formik>
  );
}

export const FormikForm = forwardRef(FormikFormInner) as <
  Values extends FormikValues
>(
  props: {
    children:
      | ((props: TypedFormikProps<Values>) => React.ReactNode)
      | React.ReactNode;
    onSubmit: (
      values: Values,
      formikHelpers?: FormikHelpers<Values>
    ) => void | Promise<any>;
    initialValues: Values;
    validationSchema?: FormikComponentProps["validationSchema"];
    enableReinitialize?: FormikComponentProps["enableReinitialize"];
    validateOnMount?: FormikComponentProps["validateOnMount"];
    validateOnChange?: FormikComponentProps["validateOnChange"];
    validateOnBlur?: FormikComponentProps["validateOnBlur"];
    validationContext?: any;
    noValidate?: boolean;
    className?: string;
  } & { ref?: Ref<HTMLFormElement> }
) => React.ReactElement;
